/**
 * Activity (admin only): the field-level audit trail (GET api/members/{id}/audit). One row per
 * changed field; rows from the same save are grouped.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { IconAlertCircle, IconHistory, IconArrowRight } from '@tabler/icons-react';
import { Card, Stack, Group, Text, Alert, Skeleton, Badge, Select, EmptyState, Divider } from '@/components/ui';
import { useMemberService, type MemberFieldChangeAudit } from '@actschurch/ccms-sdk';
import type { MemberTabProps } from '../types';
import { UserName } from '../UserName';
import { useMaritalStatuses, useMemberTypes } from '../useMemberLookups';
import { auditFieldLabel, auditSource, errorMessage, formatStampTime, formatDob } from '../memberFormat';

interface AuditGroup { key: string; at: number; source: string; sourceRef?: string | null; changedBy?: string | null; rows: MemberFieldChangeAudit[] }

export function ActivityTab({ api, member, perms }: MemberTabProps) {
  const memberService = useMemberService();
  const [limit, setLimit] = useState('200');
  const audit = useQuery({
    queryKey: [...api.keys.audit, limit],
    queryFn: () => memberService.getAudit(member.memberID, Number(limit)),
    enabled: perms.audit,
  });
  const { statuses } = useMaritalStatuses();
  const { types } = useMemberTypes();

  const groups = useMemo<AuditGroup[]>(() => {
    const out: AuditGroup[] = [];
    const rows = [...(audit.data ?? [])].sort((a, b) => Number(b.createdDateTime) - Number(a.createdDateTime) || b.memberFieldChangeAuditID - a.memberFieldChangeAuditID);
    for (const r of rows) {
      const key = `${r.createdDateTime}|${r.source}|${r.changedBy ?? ''}|${r.sourceRef ?? ''}`;
      const last = out[out.length - 1];
      if (last && last.key === key) last.rows.push(r);
      else out.push({ key, at: Number(r.createdDateTime), source: r.source, sourceRef: r.sourceRef, changedBy: r.changedBy, rows: [r] });
    }
    return out;
  }, [audit.data]);

  const display = (field: string, value?: string | null): string => {
    if (value === null || value === undefined || value === '') return 'empty';
    const f = field.toLowerCase();
    if (f === 'maritalstatusid') return statuses.find((s) => String(s.maritalStatusID) === value)?.name ?? (value === '0' ? 'not set' : value);
    if (f === 'typeid') return types.find((t) => String(t.typeID) === value)?.name ?? value;
    if (f === 'dateofbirth') return formatDob(value);
    if (value === 'true') return 'Yes';
    if (value === 'false') return 'No';
    return value;
  };

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text fw={600}>Activity</Text>
            <Text size="sm" c="dimmed">Every change to this record: who made it, where it came from and what changed.</Text>
          </div>
          <Select size="xs" w={150} data={[{ value: '50', label: 'Last 50' }, { value: '200', label: 'Last 200' }, { value: '1000', label: 'Last 1000' }]}
            value={limit} onChange={(v) => setLimit(v ?? '200')} allowDeselect={false} />
        </Group>

        {audit.isLoading && <Stack gap="sm"><Skeleton height={60} /><Skeleton height={60} /><Skeleton height={60} /></Stack>}
        {audit.isError && <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not load the activity">{errorMessage(audit.error)}</Alert>}
        {!audit.isLoading && !audit.isError && groups.length === 0 && (
          <EmptyState size="sm" icon={<IconHistory size={32} />} title="No recorded changes"
            description="Changes are recorded from the day the audit trail was switched on." />
        )}

        <Stack gap={0}>
          {groups.map((g, i) => {
            const src = auditSource(g.source);
            return (
              <div key={`${g.key}-${i}`}>
                {i > 0 && <Divider my="sm" />}
                <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
                  <Group gap="xs">
                    <Badge variant="light" color={src.color}>{src.label}</Badge>
                    <Text size="sm">by <UserName userId={g.changedBy} fallback="someone" /></Text>
                    {g.sourceRef && <Text size="xs" c="dimmed">({g.sourceRef})</Text>}
                  </Group>
                  <Text size="xs" c="dimmed">{formatStampTime(g.at)}</Text>
                </Group>
                <Stack gap={4} mt={6}>
                  {g.rows.map((r) => (
                    <Group key={r.memberFieldChangeAuditID} gap={6} wrap="wrap">
                      <Text size="sm" fw={500}>{auditFieldLabel(r.fieldName)}:</Text>
                      <Text size="sm" c="dimmed" td="line-through">{display(r.fieldName, r.oldValue)}</Text>
                      <IconArrowRight size={14} style={{ opacity: 0.6 }} />
                      <Text size="sm">{display(r.fieldName, r.newValue)}</Text>
                    </Group>
                  ))}
                </Stack>
              </div>
            );
          })}
        </Stack>
      </Stack>
    </Card>
  );
}
